import { useEffect, useState } from "react";
import { Copy, Plus, Trash2 } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminListDiscountCodes, adminCreateDiscountCode, adminDeleteDiscountCode } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminDiscountCodes() {
  const { toast } = useToast();
  const [codes, setCodes] = useState([]);
  const [percent, setPercent] = useState(20);
  const [days, setDays] = useState(90);
  const [email, setEmail] = useState("");

  function load() {
    adminListDiscountCodes().then((d) => setCodes(d.codes || []));
  }
  useEffect(() => { load(); }, []);

  async function create() {
    await adminCreateDiscountCode({ percent_off: Number(percent), valid_days: Number(days), email: email || undefined });
    toast({ title: "Code aangemaakt" });
    setEmail("");
    load();
  }

  async function remove(code) {
    if (!window.confirm(`Verwijder kortingscode ${code}?`)) return;
    try {
      await adminDeleteDiscountCode(code);
      toast({ title: "Code verwijderd", description: code });
      load();
    } catch (err) {
      toast({ title: "Verwijderen mislukt", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    }
  }

  function copy(code) {
    navigator.clipboard.writeText(code);
    toast({ title: "Code gekopieerd", description: code });
  }

  return (
    <AdminLayout>
      <div data-testid="admin-codes-page">
        <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Kortingscodes</h1>
        <p className="text-ink-soft mt-1 text-sm">Refer-a-friend codes worden automatisch gegenereerd. Hier kan je manueel codes maken.</p>

        <div className="mt-6 rounded-2xl bg-white border border-border p-6 grid sm:grid-cols-4 gap-4 items-end">
          <div>
            <Label htmlFor="percent">Korting %</Label>
            <Input id="percent" type="number" min={1} max={100} value={percent} onChange={(e) => setPercent(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="days">Geldig (dagen)</Label>
            <Input id="days" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ce-email">E-mail (optioneel)</Label>
            <Input id="ce-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="leeg = generiek" />
          </div>
          <button
            onClick={create}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold"
            data-testid="create-code"
          >
            <Plus className="h-4 w-4" /> Maak code
          </button>
        </div>

        <div className="mt-6 rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-ink-soft text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Code</th>
                <th className="px-4 py-3 text-left">Korting</th>
                <th className="px-4 py-3 text-left">E-mail</th>
                <th className="px-4 py-3 text-left">Geldig tot</th>
                <th className="px-4 py-3 text-left">Gebruikt</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-soft">Geen codes.</td></tr>
              ) : (
                codes.map((c) => (
                  <tr key={c.code} className="border-t border-border">
                    <td className="px-4 py-3 font-mono font-bold text-ink">{c.code}</td>
                    <td className="px-4 py-3">{c.percent_off}%</td>
                    <td className="px-4 py-3 text-ink-soft">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-ink-soft">{c.valid_until_iso?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      {c.used ? <span className="text-red-600">Gebruikt</span> : <span className="text-emerald-600">Open</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3 justify-end">
                        <button onClick={() => copy(c.code)} className="text-[#002FA7] inline-flex items-center gap-1 text-xs" data-testid={`copy-${c.code}`}>
                          <Copy className="h-3.5 w-3.5" /> Kopieer
                        </button>
                        <button
                          onClick={() => remove(c.code)}
                          className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs"
                          data-testid={`delete-${c.code}`}
                          aria-label={`Verwijder ${c.code}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Verwijder
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
